/**
 * overlay-compositor.js — shared compositing core for /video-overlay.
 *
 * Stages (all GPU-backed where possible):
 *   fg frame ── keyer ──▶ keyed RGBA (alpha)      chroma: WebGL shader (CbCr
 *            (chroma/AI)                           distance, OBS-style) with a
 *                                                  CPU fallback; AI: mask apply
 *                                                  via destination-in + blur.
 *   keyed ──── shaper ──▶ shaped canvas            rect / rounded / circle clip,
 *                                                  cover-fit inside the shape,
 *                                                  optional border stroke.
 *   shaped ─── placer ──▶ output ctx2d             translate/rotate/scale,
 *                                                  opacity, blend mode, shadow
 *                                                  (canvas shadows follow the
 *                                                  source alpha = true silhouette
 *                                                  shadow on keyed footage).
 *
 * Pure module: no DOM lookups, no telemetry — the blade owns UI and events.
 * Exposed as window.OverlayCompositor. Used by both the live preview loop and
 * the export drawFrame, so preview and file stay pixel-identical (WYSIWYG).
 */
(function () {
'use strict';
if (window.OverlayCompositor) return;

/* ── chroma in CbCr ──────────────────────────────────────────────
   Keying on chroma (Cb,Cr) ignores luminance, so shadows/highlights on the
   green screen key out together — the classic RGB-distance approach the old
   tool used treated a dark-green fold as "not the key color" and left fringes.
   BT.601 full-range coefficients; absolute Cb/Cr in 0..1. */
function rgb2CbCr(r, g, b) {
    return [
        0.5 - 0.168736 * r - 0.331264 * g + 0.5 * b,
        0.5 + 0.5 * r - 0.418688 * g - 0.081312 * b
    ];
}

var VERT = [
    'attribute vec2 a_pos;',
    'varying vec2 v_uv;',
    'void main(){',
    '  v_uv = a_pos * 0.5 + 0.5;',
    // flip Y: canvas textures arrive top-down
    '  gl_Position = vec4(a_pos.x, -a_pos.y, 0.0, 1.0);',
    '}'
].join('\n');

var FRAG = [
    'precision mediump float;',
    'varying vec2 v_uv;',
    'uniform sampler2D u_tex;',
    'uniform vec2 u_key;',        // key color in CbCr
    'uniform float u_similarity;',// alpha=0 below this chroma distance
    'uniform float u_smoothness;',// transition band width
    'uniform float u_spill;',     // 0..1 spill suppression strength
    'uniform vec3 u_keyCh;',      // one-hot: which RGB channel the key dominates
    'uniform vec3 u_othA;',
    'uniform vec3 u_othB;',
    'vec2 cbcr(vec3 c){',
    '  return vec2(0.5 - 0.168736*c.r - 0.331264*c.g + 0.5*c.b,',
    '              0.5 + 0.5*c.r - 0.418688*c.g - 0.081312*c.b);',
    '}',
    'void main(){',
    '  vec4 px = texture2D(u_tex, v_uv);',
    '  float d = distance(cbcr(px.rgb), u_key) * 2.0;',
    '  float alpha = smoothstep(u_similarity, u_similarity + max(u_smoothness, 0.001), d);',
    '  vec3 rgb = px.rgb;',
    '  if (u_spill > 0.001) {',
    '    float k = dot(rgb, u_keyCh);',
    '    float o = max(dot(rgb, u_othA), dot(rgb, u_othB));',
    '    float excess = max(k - o, 0.0);',
    '    rgb -= u_keyCh * (excess * u_spill);',
    '  }',
    // premultiply — 2D canvas expects premultiplied alpha from WebGL
    '  gl_FragColor = vec4(rgb * alpha, alpha);',
    '}'
].join('\n');

function compileShader(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        var log = gl.getShaderInfoLog(s);
        gl.deleteShader(s);
        throw new Error('shader: ' + log);
    }
    return s;
}

function createGlKeyer() {
    var canvas = document.createElement('canvas');
    canvas.width = 2; canvas.height = 2;
    var gl = canvas.getContext('webgl', { premultipliedAlpha: true, preserveDrawingBuffer: false, antialias: false })
          || canvas.getContext('experimental-webgl', { premultipliedAlpha: true });
    if (!gl) return null;
    var prog;
    try {
        var vs = compileShader(gl, gl.VERTEX_SHADER, VERT);
        var fs = compileShader(gl, gl.FRAGMENT_SHADER, FRAG);
        prog = gl.createProgram();
        gl.attachShader(prog, vs);
        gl.attachShader(prog, fs);
        gl.linkProgram(prog);
        if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error('link: ' + gl.getProgramInfoLog(prog));
    } catch (e) {
        return null;
    }
    gl.useProgram(prog);
    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, 'a_pos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    var uni = {
        key: gl.getUniformLocation(prog, 'u_key'),
        similarity: gl.getUniformLocation(prog, 'u_similarity'),
        smoothness: gl.getUniformLocation(prog, 'u_smoothness'),
        spill: gl.getUniformLocation(prog, 'u_spill'),
        keyCh: gl.getUniformLocation(prog, 'u_keyCh'),
        othA: gl.getUniformLocation(prog, 'u_othA'),
        othB: gl.getUniformLocation(prog, 'u_othB')
    };
    return {
        canvas: canvas,
        lost: function () { return gl.isContextLost && gl.isContextLost(); },
        render: function (source, w, h, p) {
            if (canvas.width !== w || canvas.height !== h) {
                canvas.width = w; canvas.height = h;
            }
            gl.viewport(0, 0, w, h);
            gl.bindTexture(gl.TEXTURE_2D, tex);
            gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
            /* An HLG/PQ layer uploads as its raw coded signal — and the key colour
               is picked on the SDR picture: the HDR → SDR door (hdr-to-sdr.js,
               docs/hdr-to-sdr.md) hands that picture, or the source itself. */
            if (window.HdrToSdr && ((typeof VideoFrame !== 'undefined' && source instanceof VideoFrame) ||
                (typeof HTMLVideoElement !== 'undefined' && source instanceof HTMLVideoElement))) {
                source = window.HdrToSdr.image(source, { texture: true });
            }
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
            var key = rgb2CbCr(p.keyColor[0] / 255, p.keyColor[1] / 255, p.keyColor[2] / 255);
            gl.uniform2f(uni.key, key[0], key[1]);
            gl.uniform1f(uni.similarity, p.similarity);
            gl.uniform1f(uni.smoothness, p.smoothness);
            gl.uniform1f(uni.spill, p.spill);
            var kc = dominantChannel(p.keyColor);
            gl.uniform3f(uni.keyCh, kc === 0 ? 1 : 0, kc === 1 ? 1 : 0, kc === 2 ? 1 : 0);
            gl.uniform3f(uni.othA, kc === 0 ? 0 : 1, kc === 0 ? 1 : 0, 0);
            gl.uniform3f(uni.othB, 0, kc === 2 ? 1 : 0, kc === 2 ? 0 : 1);
            gl.drawArrays(gl.TRIANGLES, 0, 3);
            return canvas;
        }
    };
}

function dominantChannel(rgb) {
    if (rgb[1] >= rgb[0] && rgb[1] >= rgb[2]) return 1;
    if (rgb[2] >= rgb[0] && rgb[2] >= rgb[1]) return 2;
    return 0;
}

/* CPU fallback — same math as the shader, ~10-30 ms/frame at 720p. Only used
   when WebGL is unavailable or the context is lost mid-session. */
function cpuChroma(ctx, w, h, p) {
    var img = ctx.getImageData(0, 0, w, h);
    var d = img.data;
    var key = rgb2CbCr(p.keyColor[0] / 255, p.keyColor[1] / 255, p.keyColor[2] / 255);
    var sim = p.similarity, smooth = Math.max(p.smoothness, 0.001), spill = p.spill;
    var kc = dominantChannel(p.keyColor);
    var oA = kc === 0 ? 1 : 0, oB = kc === 2 ? 1 : 2;
    if (kc === 1) { oA = 0; oB = 2; }
    if (kc === 2) { oA = 0; oB = 1; }
    for (var i = 0; i < d.length; i += 4) {
        var r = d[i] / 255, g = d[i + 1] / 255, b = d[i + 2] / 255;
        var cb = 0.5 - 0.168736 * r - 0.331264 * g + 0.5 * b;
        var cr = 0.5 + 0.5 * r - 0.418688 * g - 0.081312 * b;
        var dist = Math.sqrt((cb - key[0]) * (cb - key[0]) + (cr - key[1]) * (cr - key[1])) * 2;
        var t = (dist - sim) / smooth;
        var alpha = t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t); // smoothstep
        if (spill > 0.001 && alpha > 0) {
            var ch = [d[i], d[i + 1], d[i + 2]];
            var excess = ch[kc] - Math.max(ch[oA], ch[oB]);
            if (excess > 0) ch[kc] -= excess * spill;
            d[i] = ch[0]; d[i + 1] = ch[1]; d[i + 2] = ch[2];
        }
        d[i + 3] = Math.round(alpha * 255);
    }
    ctx.putImageData(img, 0, 0);
}

/* ── layout ──────────────────────────────────────────────────────
   layer: { x, y (normalized center 0..1), scale (fraction of output WIDTH),
            rotation (deg), fit: 'custom'|'cover'|'contain' }
   Aspect ratio of the overlay source is always preserved. */
function layerRect(outW, outH, srcW, srcH, layer) {
    var aspect = (srcH && srcW) ? srcH / srcW : 9 / 16;
    if (layer.fit === 'cover' || layer.fit === 'contain') {
        var s = layer.fit === 'cover'
            ? Math.max(outW / srcW, outH / srcH)
            : Math.min(outW / srcW, outH / srcH);
        var w0 = srcW * s, h0 = srcH * s;
        return { cx: outW / 2, cy: outH / 2, w: w0, h: h0, rotation: 0 };
    }
    var w = Math.max(8, layer.scale * outW);
    var h = w * aspect;
    return { cx: layer.x * outW, cy: layer.y * outH, w: w, h: h, rotation: layer.rotation || 0 };
}

/* Shape path in local coords centered at 0,0. radius: 0..0.5 of min side. */
function tracePath(ctx, w, h, shape, radius) {
    var x = -w / 2, y = -h / 2;
    ctx.beginPath();
    if (shape === 'circle') {
        var d = Math.min(w, h);
        ctx.arc(0, 0, d / 2, 0, Math.PI * 2);
    } else if (shape === 'rounded' && radius > 0) {
        var r = Math.min(w, h) * Math.min(0.5, radius);
        if (ctx.roundRect) ctx.roundRect(x, y, w, h, r);
        else { // Safari <16 fallback
            ctx.moveTo(x + r, y);
            ctx.arcTo(x + w, y, x + w, y + h, r);
            ctx.arcTo(x + w, y + h, x, y + h, r);
            ctx.arcTo(x, y + h, x, y, r);
            ctx.arcTo(x, y, x + w, y, r);
            ctx.closePath();
        }
    } else {
        ctx.rect(x, y, w, h);
    }
}

function create() {
    var glKeyer = createGlKeyer();
    // staging canvases (lazily sized)
    var keyC = document.createElement('canvas'), keyCtx = keyC.getContext('2d', { willReadFrequently: !glKeyer });
    var chromaTainted = false;
    var maskC = document.createElement('canvas'), maskCtx = maskC.getContext('2d');
    var shapeC = document.createElement('canvas'), shapeCtx = shapeC.getContext('2d');

    function size(c, w, h) { if (c.width !== w) c.width = w; if (c.height !== h) c.height = h; }

    /* keyFrame: fg source → canvas with alpha.
       params.mode: 'none' | 'chroma' | 'mask'
       chroma: {keyColor:[r,g,b], similarity, smoothness, spill} all 0..1 except color
       mask:   {maskSource (canvas/image with person=white), maskBlur px} */
    function keyFrame(source, w, h, params) {
        if (params.mode === 'chroma') {
            if (glKeyer && !glKeyer.lost()) {
                try { return glKeyer.render(source, w, h, params); }
                catch (e) { /* fall through to CPU */ }
            }
            size(keyC, w, h);
            keyCtx.clearRect(0, 0, w, h);
            keyCtx.drawImage(source, 0, 0, w, h);
            if (!chromaTainted) {
                try {
                    cpuChroma(keyCtx, w, h, params);
                } catch (e) {
                    if (e && e.name === 'SecurityError') {
                        /* Some Android browsers (Yandex 26 / Android 14,
                           TIMBRICA-FRONTEND-F0) mislabel a same-origin blob:
                           video as cross-origin and taint the canvas — every
                           source here comes from the user's own File, so the
                           taint is a browser quirk we cannot clear. Keying
                           stays off for the rest of the session (the overlay
                           keeps compositing unkeyed) instead of throwing out
                           of the render loop on every frame. */
                        chromaTainted = true;
                        try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'chroma_tainted_canvas' }); } catch (_) {}
                    } else {
                        throw e;
                    }
                }
            }
            return keyC;
        }
        if (params.mode === 'mask' && params.maskSource) {
            size(keyC, w, h);
            keyCtx.clearRect(0, 0, w, h);
            keyCtx.drawImage(source, 0, 0, w, h);
            keyCtx.save();
            keyCtx.globalCompositeOperation = 'destination-in';
            if (params.maskBlur > 0) keyCtx.filter = 'blur(' + params.maskBlur + 'px)';
            keyCtx.drawImage(params.maskSource, 0, 0, w, h);
            keyCtx.restore();
            return keyC;
        }
        return source;
    }

    /* drawComposite: full frame onto ctx (already sized outW×outH).
       opts = {
         bg: source, bgW, bgH,
         fg: null | {
           source, srcW, srcH,
           key: keyFrame params (or {mode:'none'}),
           layer: {x,y,scale,rotation,opacity,blend,shape,radius,
                   border,borderColor,shadow,flipH,fit},
           alphaMul: 0..1   // timing fade multiplier
         }
       } */
    /* A video layer in its SDR picture (the HDR → SDR door, docs/hdr-to-sdr.md):
       the browser's own 2D picture of an HLG/PQ clip is the dim one (measured
       22.10.2026: preview 88 against 128 for the same SDR picture). The door's
       canvas is reused — each layer is drawn before the next one is converted. */
    function sdrOf(src) {
        if (!window.HdrToSdr || !src) return src;
        if ((typeof VideoFrame !== 'undefined' && src instanceof VideoFrame) ||
            (typeof HTMLVideoElement !== 'undefined' && src instanceof HTMLVideoElement)) return window.HdrToSdr.image(src);
        return src;
    }
    function drawComposite(ctx, outW, outH, opts) {
        ctx.clearRect(0, 0, outW, outH);
        if (opts.bg) ctx.drawImage(sdrOf(opts.bg), 0, 0, outW, outH);
        var fg = opts.fg;
        if (!fg || !fg.source || fg.alphaMul <= 0) return;
        var L = fg.layer;
        var keyed = keyFrame(sdrOf(fg.source), fg.srcW, fg.srcH, fg.key || { mode: 'none' });

        var rect = layerRect(outW, outH, fg.srcW, fg.srcH, L);
        var needShape = (L.shape === 'circle') || (L.shape === 'rounded' && L.radius > 0.002);

        // shaper: clip keyed footage into the shape at target size (cover-fit
        // for circle so the disc is filled edge-to-edge)
        var drawSrc = keyed, sw = fg.srcW, sh = fg.srcH;
        var dw = rect.w, dh = rect.h;
        if (needShape) {
            var cw = Math.max(2, Math.round(rect.w)), chh = Math.max(2, Math.round(rect.h));
            if (L.shape === 'circle') { cw = chh = Math.max(2, Math.round(Math.min(rect.w, rect.h))); }
            size(shapeC, cw, chh);
            shapeCtx.clearRect(0, 0, cw, chh);
            shapeCtx.save();
            shapeCtx.translate(cw / 2, chh / 2);
            tracePath(shapeCtx, cw, chh, L.shape, L.radius);
            shapeCtx.clip();
            // cover-fit inside the shape box
            var s = Math.max(cw / fg.srcW, chh / fg.srcH);
            var iw = fg.srcW * s, ih = fg.srcH * s;
            shapeCtx.drawImage(keyed, -iw / 2, -ih / 2, iw, ih);
            shapeCtx.restore();
            if (L.border) {
                shapeCtx.save();
                shapeCtx.translate(cw / 2, chh / 2);
                var bw = Math.max(2, Math.min(cw, chh) * 0.012 * (L.borderWidth || 3));
                tracePath(shapeCtx, cw - bw, chh - bw, L.shape, L.radius);
                shapeCtx.lineWidth = bw;
                shapeCtx.strokeStyle = L.borderColor || '#ffffff';
                shapeCtx.stroke();
                shapeCtx.restore();
            }
            drawSrc = shapeC; sw = cw; sh = chh; dw = cw; dh = chh;
        }

        ctx.save();
        ctx.translate(rect.cx, rect.cy);
        if (rect.rotation) ctx.rotate(rect.rotation * Math.PI / 180);
        if (L.flipH) ctx.scale(-1, 1);
        ctx.globalAlpha = Math.max(0, Math.min(1, (L.opacity == null ? 1 : L.opacity) * (fg.alphaMul == null ? 1 : fg.alphaMul)));
        ctx.globalCompositeOperation = L.blend && L.blend !== 'normal' ? L.blend : 'source-over';
        if (L.shadow) {
            ctx.shadowColor = 'rgba(0,0,0,0.5)';
            ctx.shadowBlur = Math.max(6, Math.min(outW, outH) * 0.02);
            ctx.shadowOffsetY = Math.max(2, Math.min(outW, outH) * 0.006);
        }
        ctx.drawImage(drawSrc, 0, 0, sw, sh, -dw / 2, -dh / 2, dw, dh);
        // border for un-shaped rect (shaped border baked in shaper above)
        if (L.border && !needShape) {
            ctx.shadowColor = 'transparent';
            var bw2 = Math.max(2, Math.min(dw, dh) * 0.012 * (L.borderWidth || 3));
            tracePath(ctx, dw - bw2, dh - bw2, 'rect', 0);
            ctx.lineWidth = bw2;
            ctx.strokeStyle = L.borderColor || '#ffffff';
            ctx.stroke();
        }
        ctx.restore();
    }

    return {
        chromaEngine: glKeyer ? 'webgl' : 'cpu',
        keyFrame: keyFrame,
        drawComposite: drawComposite,
        layerRect: layerRect
    };
}

window.OverlayCompositor = {
    create: create,
    layerRect: layerRect,   // exported for hit-testing in the page
    _rgb2CbCr: rgb2CbCr     // exported for tests
};

})();
